import { FC } from 'react';
import millify from 'millify';
import {
	DashboardPerformerSkeleton,
	DashboardPerformersStats,
	StatSparklineChart,
	StatSparklineChartSkeleton,
	DashboardCellRendererFrame,
	NoValuePlaceholder,
} from '@aleph/shared-components';
import useDashboardStats from 'wb-reddit/app/routes/superGrid/hooks/useDashboardStats';
import { RedditSuperGridStatsFragmentFragment } from 'wb-reddit/graphql/generated';
import { LEVEL_ID_FILTER } from 'wb-reddit/app/routes/superGrid/actionRow/filters/utils';
import { VARIABLES } from 'wb-reddit/styles/variables';
import { ICellRendererParams } from 'ag-grid-community';
import { useNavigate, useParams } from 'react-router-dom';

const StatCellRenderer: FC<ICellRendererParams & {
	currency?: string,
	currencyDivisor?: number,
	fractionDigits?: number
	isRate?: boolean,
}> = (params) => {
	const navigate = useNavigate();
	const isDashboardView = params.data?.isDashboardView && !params.node.expanded;
	const dividedValue = ((Number(params.value ?? 0) / (params.currencyDivisor || 1)) * (params.isRate ? 100 : 1));

	const focusedCell = params.api?.getFocusedCell();
	const isFocusedOnThisRow = focusedCell?.rowIndex === params.rowIndex;
	const isFocused = isFocusedOnThisRow && focusedCell?.column === params.column;

	const {
		reloading, loading, stats, worstPerformers, bestPerformers,
	} = useDashboardStats({
		parentFilter: {
			[LEVEL_ID_FILTER[params.data?.level ?? '']]: [{ value: [params.data?.id] }],
		},
		focusedCell: isFocusedOnThisRow ? focusedCell : null,
		selectedKPI: params?.data?.clientKpi,
		skip:        !isDashboardView,
	});

	const fieldName = params.colDef?.field ?? '';

	const dividedStats = stats?.map((statEntry) => ({
		...statEntry,
		[fieldName]: statEntry[fieldName] / (params.currencyDivisor || 1),
	}));

	const dividedBestPerformers = bestPerformers?.map((performer) => ({
		...performer,
		currStats: performer?.currStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
		lastStats: performer?.lastStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
	}));

	const divideWorstPerformers = worstPerformers?.map((performer) => ({
		...performer,
		currStats: performer?.currStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
		lastStats: performer?.lastStats?.map((statEntry) => ({
			...statEntry,
			[fieldName]: (statEntry?.[fieldName] ?? 0) / (params.currencyDivisor || 1),
		})) ?? [],
	}));

	return (
		<DashboardCellRendererFrame>
			<div className="flex items-center justify-between h-full px-[8px] gap-[8px]">
				{!!(params.value && params.currency) && <div className="text-gray-700 text-[12px]">{params.currency}</div>}
				{!!(params.value && params.isRate) && <div className="text-gray-700 text-[12px]">%</div>}
				{!(params.value && params.isRate) && <div/>}

				{
					params.value
						? (isDashboardView ? millify(dividedValue) : Number(dividedValue).toLocaleString(undefined, {
							minimumFractionDigits: params.fractionDigits ?? 0,
							maximumFractionDigits: params.fractionDigits ?? 0,
						}))
						: <NoValuePlaceholder />
				}
			</div>

			{
				isDashboardView
				&& <div
					className="p-[12px] max-h-full h-full"
					key={params.column?.getActualWidth()}
				>
					{loading && <StatSparklineChartSkeleton />}
					{
						stats && !loading
						&& <StatSparklineChart
							data={dividedStats}
							field={
								(params.colDef?.field ?? 'impressions') as keyof RedditSuperGridStatsFragmentFragment
							}
							color={VARIABLES.COLORS.PARTNER[500]}
							navigate={navigate}
							useParams={useParams}
						/>
					}
				</div>
			}

			{
				isDashboardView
				&& <div className="w-full h-full">
					{
						(reloading && isFocused)
						&& <DashboardPerformerSkeleton />
					}

					{
						dividedBestPerformers
					&& <DashboardPerformersStats
						field={fieldName}
						isRate={params.isRate}
						data={dividedBestPerformers}
					/>
					}
				</div>
			}

			{
				isDashboardView
				&& <div className="w-full h-full">
					{
						(reloading && isFocused)
						&& <DashboardPerformerSkeleton />
					}

					{
						divideWorstPerformers
						&& <DashboardPerformersStats
							field={fieldName}
							isRate={params.isRate}
							data={divideWorstPerformers}
						/>
					}
				</div>
			}
		</DashboardCellRendererFrame>
	);
};

export default StatCellRenderer;
